import { isNil } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import createNetworkingClient from 'utils/networking-client';
import { injectGoogleScript } from '../externalOauthLibs';

// TODO [fumba] - change the scope to https://www.googleapis.com/auth/contacts.readonly
// once strava app is verified by google.
const SCOPE = 'https://www.googleapis.com/auth/userinfo.profile';

const PEOPLE_BASE_URL = 'https://people.googleapis.com/v1/';

/**
 * Fetches a list of contacts from an authenticated google account.
 */
const useGoogle = ({ clientId, mocked = false }) => {
  const [tokenClient, setTokenClient] = useState(undefined);
  const [gsiScriptLoaded, setGsiScriptLoaded] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [error, setError] = useState(null);
  const [isLinked, setIsLinked] = useState(false);
  const [token, setToken] = useState(undefined);

  const logError = (e) => {
    setError(e);
    // TODO log error in monorepo sentry
  };

  // TODO [fumba] - remove this mocked response once strava app gets verified by google
  const setMockContacts = () => {
    import('./data/google_connections_sample.json').then((c) => {
      setContacts(c.connections);
    });
  };

  /**
   *  callback method called when the athlete signs into their google account.
   *  This method attempts to retrieve contacts from the authenticated google account.
   */
  const handleGoogleSignIn = useCallback(
    (tokenResponse) => {
      const accessToken = tokenResponse.access_token;
      setToken(accessToken);

      const headers = { Authorization: `Bearer ${accessToken}` };

      const networkingClient = createNetworkingClient({ headers });
      try {
        const url = mocked
          ? 'https://www.googleapis.com/oauth2/v3/userinfo'
          : `${PEOPLE_BASE_URL}people/me/connections?personFields=names,photos,emailAddresses`;
        networkingClient
          .get(url, { params: { alt: 'json' } })
          .then((response) => {
            if (response && response.status === 200) {
              setIsLinked(true);
              const { connections } = response.data;
              if (mocked) {
                setMockContacts();
                return;
              }
              if (connections.length) {
                setContacts(connections);
              } else {
                setContacts([]); // google user has no connections
              }
            } else {
              logError(response.error); // responses other than 200
            }
          })
          .catch((e) => {
            logError(e);
          });
      } catch (e) {
        logError(e);
      }
    },
    [mocked]
  );

  /**
   * Initializes a client that is used by athletes to request a token
   */
  const initializeGsiTokenClient = useCallback(() => {
    if (tokenClient) return tokenClient;
    setGsiScriptLoaded(true);
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: SCOPE,
      callback: handleGoogleSignIn
    });
    setTokenClient(client);
    return client;
  }, [clientId, handleGoogleSignIn, tokenClient]);

  /**
   * injects GSI javascript library when the component that uses this hook is mounted
   */
  useEffect(() => {
    injectGoogleScript(initializeGsiTokenClient);

    return () => {
      window.google.accounts.id.cancel();
    };
  }, [gsiScriptLoaded, initializeGsiTokenClient]);

  const requestContacts = () => {
    const client = initializeGsiTokenClient(); // incase request is made before script.onload callback is triggered
    setError(null);
    if (client) {
      client.requestAccessToken(handleGoogleSignIn); // param is only used by unit tests
    } else {
      const e = new Error('google TokenClient not initialized');
      logError(e);
    }
  };

  const disconnect = () => {
    setIsLinked(false);
    setContacts([]);
    setError(null);
    if (!isNil(token)) {
      window.google.accounts.oauth2.revoke(token);
    }
  };

  return { error, contacts, requestContacts, isLinked, disconnect };
};

export default useGoogle;
